import api from './api'
import type { WorkerRetryState } from '../utils/renderProgress'

export interface ValidatorIssue {
  category: string
  message: string
  fix?: string | null
  example?: string | null
  severity?: string
  // 1-based conversation message the issue points at (null when not anchored to
  // a single message). Lets the editor bind the error to the exact step.
  message_index?: number | null
  // Authored element name of the message this issue points at (e.g. "MapsFrames"),
  // resolved server-side from the conversation. Already folded into `message`;
  // exposed separately so the UI can label without parsing prose.
  element?: string | null
  // Stable identifier for this class of problem, straight from the renderer's
  // structured verdict. Prefer keying help text and repairs off `code` rather
  // than matching on `message`, which is prose and gets rewritten for display.
  code?: string | null
  // Dotted path inside the payload the issue points at ('payload.title').
  field?: string | null
  suggested_action?: 'richlink_style_small' | string | null
}

export interface ValidatorSummaryItem {
  category: string
  count: number
}

export type MockSort = 'newest' | 'oldest' | 'name' | 'recently_edited'

/** How far an archive reaches. Personal unless an admin says otherwise. */
export type ArchiveScope = 'me' | 'organization'

export interface MockListFilters {
  createdBy?: string
  lastEditedBy?: string
  query?: string
  excludeStatus?: string
  tag?: string
  sort?: MockSort
  /** true: has a render output. false: never rendered. undefined: either. */
  rendered?: boolean
  /**
   * The library's two tabs, from this viewer's point of view. Archived means
   * the organization archived it OR this person did; a personal archive hides
   * it only from them.
   */
  archived?: boolean
}

export interface MockListPage {
  mocks: Mock[]
  total: number
  skip: number
  limit: number
}

export interface MockFilterPerson {
  id: string
  name: string
}

export interface MockFilterOptions {
  archived_count: number
  total_count: number
  tags: string[]
  creators: MockFilterPerson[]
  last_editors: MockFilterPerson[]
}

export interface Mock {
  id: string
  title: string
  slug: string
  description?: string
  brand_id?: string
  conversation_json: any
  ios_version: 'ios18' | 'ios26'
  status: 'draft' | 'published' | 'archived'
  vertical?: string
  use_case?: string
  tags: string[]
  editor_metadata?: EditorMetadata
  current_version: number
  thumbnail_url?: string
  output_gif_url?: string
  output_mp4_url?: string
  output_png_url?: string
  created_at: string
  updated_at: string
  created_by?: string
  created_by_name?: string
  access_level?: 'owner' | 'organization_admin' | 'view' | 'edit' | 'admin'
  is_shared_with_me?: boolean
  is_shared?: boolean
  last_edited_by?: string
  last_edited_by_name?: string
  last_edited_at?: string
  currently_editing_by?: string
  currently_editing_by_name?: string
  edit_locked_at?: string
  is_owner_locked?: boolean
  owner_locked_by?: string
  owner_locked_by_name?: string
  owner_locked_at?: string
}

export interface MockPresenceEditor {
  user_id: string
  name: string
  last_seen: string | null
  seconds_ago: number | null
}

export interface MockPresenceResponse {
  active_editors: MockPresenceEditor[]
  window_seconds: number
}

export interface MockVersion {
  id: string
  mock_id: string
  version_number: number
  version_name?: string
  change_description?: string
  conversation_json: any
  ios_version: string
  output_gif_url?: string
  output_mp4_url?: string
  output_png_url?: string
  render_job_id?: string
  version_creator?: string // Full name of whoever authored this version
  version_creator_id?: string // User ID of whoever authored this version
  // Who triggered the render recorded on this version. Separate from
  // version_creator because a render reuses an unchanged version, so the
  // author and the renderer are frequently different people.
  rendered_by_name?: string
  rendered_by_id?: string
  use_case_creator?: string // Full name of use case creator
  last_editor?: string // Full name of last editor
  created_at: string
}

export interface RenderArtifact {
  id: string
  filename: string
  kind?: 'element_preview' | 'conversation_frame' | string
  message_index?: number | null
  element_type?: string | null
  // One-line caption the renderer derived from the conversation, so a tile can
  // read "Wallet · Your pass" instead of "Wallet · message 23". May be absent:
  // element crops carry no text, and older renders predate it.
  text_preview?: string | null
  size_bytes: number
  download_url: string
}

export type MockAuditSource = 'building_blocks' | 'validator' | 'conversation_json' | 'use_case_editor'

export interface ConversationBlock {
  id: string
  block_instance_id?: string
  flow_instance_id?: string
  kind: string
  label: string
  description: string
  start_index: number
  end_index: number
  message_count: number
  conversation_elements?: any[]
  preview_image_urls?: string[]
  source_template_id?: string
  source_template_name?: string
  source_template_element_type?: string
  source_template_version?: number | string
}

export interface InsertionPoint {
  id: string
  label: string
  description: string
  insert_position: 'start' | 'end'
  insert_after_index?: number | null
  block_kind?: string
  block_label?: string
  block_description?: string
  block_message_count?: number
  source_template_id?: string
  source_template_name?: string
  source_template_element_type?: string
  source_template_version?: number | string
  source_template_message_count?: number
  is_template_insertion?: boolean
}

export interface TemplateInsertionRecord {
  id: string
  template_id: string
  template_name: string
  element_type?: string
  messages_added: number
  message_fingerprints?: string[]
  insert_position?: string
  insert_after_index?: number | null
  insertion_metadata?: Record<string, any>
  created_at?: string | null
}

export interface RenderEstimateBreakdown {
  estimated_seconds?: number
  render_timeout_seconds?: number
  message_count?: number
  element_count?: number
  estimated_generated_frames?: number
  complexity_points?: number
  encoding_seconds?: number
  output_format?: string
  queue_name?: string
  historical_basis?: string
  historical_seconds?: number
  element_counts?: Record<string, number>
}

export interface RenderTierEstimate {
  estimated_seconds: number
  estimated_generated_frames: number
  historical_basis: string
}

export interface RenderEstimateResponse {
  estimates: Record<'basic' | 'normal' | 'accurate', RenderTierEstimate>
  platformDefaults: {
    animationQuality: 'basic' | 'normal' | 'accurate'
    outputFormat: 'gif' | 'mp4' | 'both'
  }
}

export interface ConversationBlockAnalysis {
  mock_id: string
  mock_title: string
  brand_id?: string | null
  message_count: number
  normalized_messages?: any[]
  /** True when this use case still carries the legacy Business Invitations shape
   *  and a repair WOULD change it. Reading never applies the repair; the editor
   *  offers it. */
  repair_available?: boolean
  /** True when this response was fetched with repair requested and one applied. */
  repair_applied?: boolean
  blocks: ConversationBlock[]
  insertion_points: InsertionPoint[]
  editor_metadata?: EditorMetadata
  template_insertions?: TemplateInsertionRecord[]
  template_check?: {
    template_id: string
    already_added: boolean
    reason?: string | null
    template_insertion?: TemplateInsertionRecord | null
  } | null
}

export interface EditorBlockMetadata {
  block_instance_id: string
  kind: string
  start_index: number
  end_index: number
  message_fingerprints?: string[]
  flow_instance_id?: string
  // Last validation verdict for this block, plus the content fingerprint it was
  // computed against. Always read as a pair: a verdict whose fingerprint no
  // longer matches the block's content is stale and must not be shown.
  validation_status?: 'ok' | 'error' | 'advisory'
  validation_fingerprint?: string
}

export interface EditorMetadata {
  schema_version: number
  blocks: EditorBlockMetadata[]
}

export interface MockCreate {
  title: string
  description?: string
  brand_id: string
  conversation_json?: any
  ios_version?: 'ios18' | 'ios26'
  vertical?: string
  use_case?: string
  tags?: string[]
  editor_metadata?: EditorMetadata
}

export const mockService = {
  // List all mocks
  async getMocks(
    brandId?: string,
    status?: string,
    skip = 0,
    limit = 100,
    filters: MockListFilters = {},
  ): Promise<MockListPage> {
    const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() })
    if (brandId) params.append('brand_id', brandId)
    if (status) params.append('status', status)
    if (filters.excludeStatus) params.append('exclude_status', filters.excludeStatus)
    if (filters.createdBy) params.append('created_by', filters.createdBy)
    if (filters.lastEditedBy) params.append('last_edited_by', filters.lastEditedBy)
    if (filters.tag) params.append('tag', filters.tag)
    if (filters.sort) params.append('sort', filters.sort)
    // Explicitly compared to undefined: `rendered=false` is a real filter
    // ("never rendered"), and a falsy check would silently drop it.
    if (filters.rendered !== undefined) params.append('rendered', String(filters.rendered))
    if (filters.archived !== undefined) params.append('archived', String(filters.archived))
    if (filters.query) params.append('q', filters.query)

    const response = await api.get(`/mocks/?${params.toString()}`)
    return response.data
  },

  // Every accessible use case, not just the first page.
  //
  // The API caps a request at 100 rows. Pickers call this because they present
  // one scrollable list with a local search box, and a local search over the
  // first 100 rows silently cannot find use case 101. The library page pages
  // instead, which is the right answer when the user can see the pager.
  //
  // Bounded on purpose: a runaway loop against a large tenant is worse than a
  // truncated list, so this stops at maxItems and says so rather than pretending
  // the list is complete.
  async getAllMocks(
    brandId?: string,
    filters: MockListFilters = {},
    maxItems = 1000,
  ): Promise<{ mocks: Mock[]; total: number; truncated: boolean }> {
    const pageSize = 100
    const collected: Mock[] = []
    let total = 0

    for (let skip = 0; skip < maxItems; skip += pageSize) {
      const page = await this.getMocks(brandId, undefined, skip, pageSize, filters)
      total = page.total
      collected.push(...page.mocks)
      // Stop on a short page as well as on the count: a row deleted mid walk
      // would otherwise leave this asking for a page that never arrives.
      if (collected.length >= total || page.mocks.length < pageSize) break
    }

    return { mocks: collected, total, truncated: collected.length < total }
  },

  // Filter bar inputs computed over every visible use case, not over the page
  // currently on screen. A paginated list cannot count its own archived items
  // or enumerate every tag in use.
  async getMockFilterOptions(brandId?: string): Promise<MockFilterOptions> {
    const params = new URLSearchParams()
    if (brandId) params.append('brand_id', brandId)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await api.get(`/mocks/filter-options${suffix}`)
    return response.data
  },

  // Estimate render time per animation tier (no render). Powers the render
  // dialog's time preview.
  async getRenderEstimate(
    mockId: string,
    opts: { outputFormat?: string; quality?: string; gifScale?: number } = {}
  ): Promise<RenderEstimateResponse> {
    const params = new URLSearchParams()
    if (opts.outputFormat) params.append('output_format', opts.outputFormat)
    if (opts.quality) params.append('quality', opts.quality)
    if (opts.gifScale !== undefined) params.append('gif_scale', String(opts.gifScale))
    const response = await api.get(`/render/${mockId}/estimate?${params.toString()}`)
    return {
      estimates: response.data.estimates,
      platformDefaults: {
        animationQuality: response.data.platform_defaults?.animation_quality || 'basic',
        outputFormat: response.data.platform_defaults?.output_format || 'both',
      },
    }
  },

  // Get single mock
  async getMock(mockId: string): Promise<Mock> {
    const response = await api.get(`/mocks/${mockId}`)
    return response.data
  },

  async getConversationBlocks(
    mockId: string,
    templateId?: string,
    options?: { repair?: boolean },
  ): Promise<ConversationBlockAnalysis> {
    // `repair` is off by default on purpose: opening a use case must not change
    // it. The response reports `repair_available` so the editor can offer the
    // legacy Business Invitations fix as an explicit choice.
    const params = new URLSearchParams()
    if (templateId) params.set('template_id', templateId)
    if (options?.repair) params.set('repair', 'true')
    const query = params.toString() ? `?${params.toString()}` : ''
    const response = await api.get(`/mocks/${mockId}/conversation-blocks${query}`)
    return response.data
  },

  async detachTemplateInsertion(mockId: string, templateId: string): Promise<{
    detached: boolean
    deactivated: number
    asset_copy?: {
      copied?: string[]
      skipped_existing?: string[]
      missing_sources?: string[]
    }
  }> {
    const response = await api.post(`/mocks/${mockId}/template-insertions/${templateId}/detach`)
    return response.data
  },

  // Create new mock
  async createMock(mockData: MockCreate): Promise<Mock> {
    // Normalize conversation shape: allow arrays or single objects from UI
    const normalizeConversation = (conv: any): any => {
      if (!conv) return { messages: [] }
      if (Array.isArray(conv)) return { messages: conv }
      if (typeof conv === 'object' && conv.messages) return conv
      // single message object -> wrap in messages
      return { messages: [conv] }
    }

    const payload = {
      ...mockData,
      conversation_json: normalizeConversation(mockData.conversation_json),
    }

    const response = await api.post('/mocks', payload)
    return response.data
  },

  // Update mock
  async updateMock(
    mockId: string,
    updates: Partial<MockCreate> & {
      change_description?: string
      audit_source?: MockAuditSource
      status?: Mock['status']
      // Edit-conflict guard: the current_version the client last loaded. When
      // supplied and the stored version has moved on, the backend responds 409
      // instead of silently overwriting a concurrent editor. Omit to force.
      expected_version?: number
    },
  ): Promise<Mock> {
    const response = await api.put(`/mocks/${mockId}`, updates)
    return response.data
  },

  // Collaboration awareness — soft presence heartbeat + roster.
  async presenceHeartbeat(mockId: string): Promise<MockPresenceResponse> {
    const response = await api.post(`/mocks/${mockId}/presence/heartbeat`)
    return response.data
  },

  async getPresence(mockId: string): Promise<MockPresenceResponse> {
    const response = await api.get(`/mocks/${mockId}/presence`)
    return response.data
  },

  // Update mock status only
  /**
   * Archive a use case. Personal by default.
   *
   * `me` hides it from this person only and leaves colleagues untouched, which
   * is why it exists: the old global status column meant one member tidying
   * their view hid work the rest of the team still used. `organization` is the
   * admin-only, everyone-sees-it archive, and the API rejects it for others.
   */
  async archiveMockScoped(mockId: string, scope: ArchiveScope = 'me'): Promise<void> {
    await api.post(`/mocks/${mockId}/archive`, { scope })
  },

  async restoreMockScoped(mockId: string, scope: ArchiveScope = 'me'): Promise<void> {
    await api.post(`/mocks/${mockId}/restore`, { scope })
  },

  async updateMockStatus(mockId: string, status: 'draft' | 'published' | 'archived'): Promise<Mock> {
    const response = await api.put(`/mocks/${mockId}`, { status })
    return response.data
  },

  async archiveMock(mockId: string): Promise<Mock> {
    return this.updateMockStatus(mockId, 'archived')
  },

  // Delete mock
  async deleteMock(mockId: string): Promise<void> {
    await api.delete(`/mocks/${mockId}`)
  },

  // Get mock versions
  async getVersions(mockId: string): Promise<MockVersion[]> {
    const response = await api.get(`/mocks/${mockId}/versions`)
    return response.data
  },

  // Alias for version history (more descriptive name)
  async getVersionHistory(mockId: string): Promise<MockVersion[]> {
    return this.getVersions(mockId)
  },

  // Create new version
  async createVersion(
    mockId: string,
    conversationJson: any,
    changeDescription?: string
  ): Promise<MockVersion> {
    const response = await api.post(`/mocks/${mockId}/versions`, {
      conversation_json: conversationJson,
      change_description: changeDescription
    })
    return response.data
  },

  // Restore version
  async restoreVersion(mockId: string, versionId: string): Promise<Mock> {
    const response = await api.post(`/mocks/${mockId}/versions/${versionId}/restore`)
    return response.data
  },

  // Clone mock
  async cloneMock(mockId: string, newTitle?: string, newBrandId?: string): Promise<Mock> {
    // Backend expects `new_title` and `new_brand_id` (see MockClone schema)
    const response = await api.post(`/mocks/${mockId}/clone`, {
      new_title: newTitle,
      new_brand_id: newBrandId,
    })
    return response.data
  },

  // Import JSON
  async importJson(
    title: string,
    conversationJson: any,
    brandId: string,
    iosVersion: 'ios18' | 'ios26' = 'ios26'
  ): Promise<Mock> {
    const normalizeConversation = (conv: any): any => {
      if (!conv) return { messages: [] }
      if (Array.isArray(conv)) return { messages: conv }
      if (typeof conv === 'object' && conv.messages) return conv
      return { messages: [conv] }
    }

    const response = await api.post('/mocks/import', {
      title,
      conversation_json: normalizeConversation(conversationJson),
      brand_id: brandId,
      ios_version: iosVersion
    })
    return response.data
  },

  // Render mock - returns enqueued job id and reserved version id
  async renderMock(
    mockId: string,
    options?: {
      headerColor?: string
      iosVersion?: string
      headerSize?: number
      logoScale?: string
      watermark?: string
      outputFormat?: string
      animationQuality?: string  // 'basic' | 'normal' | 'accurate' (bubble-enter animation)
      quality?: string         // NEW: 'web' or 'presentation'
      gifScale?: number        // NEW: 0.5-1.0 (resolution scale)
      logs?: boolean
      cleanup?: boolean
      cleanupFramesOnly?: boolean
      singleFrame?: number
      optimizeGif?: boolean
      forceOsm?: boolean
    }
  ): Promise<{
    message?: string
    job_id?: string
    version_id?: string
    estimated_seconds?: number
    render_timeout_seconds?: number
    queue_name?: string
    estimate_breakdown?: RenderEstimateBreakdown
    osm_warning?: boolean
    mapkit_error?: string
    affected_elements?: string
    reused_active_job?: boolean
  }> {
    const params = new URLSearchParams()
    params.append('header_color', options?.headerColor || '#000000')
    if (options?.iosVersion) params.append('ios_version', options.iosVersion)
    if (options?.headerSize !== undefined) params.append('header_size', options.headerSize.toString())
    if (options?.logoScale) params.append('logo_scale', options.logoScale)
    if (options?.watermark) params.append('watermark', options.watermark)
    if (options?.outputFormat) params.append('output_format', options.outputFormat)
    if (options?.animationQuality) params.append('animation_quality', options.animationQuality)
    if (options?.quality) params.append('quality', options.quality)
    if (options?.gifScale !== undefined) params.append('gif_scale', options.gifScale.toString())
    if (options?.logs !== undefined) params.append('logs', String(options.logs))
    if (options?.cleanup !== undefined) params.append('cleanup', String(options.cleanup))
    if (options?.cleanupFramesOnly !== undefined) params.append('cleanup_frames_only', String(options.cleanupFramesOnly))
    if (options?.singleFrame !== undefined) params.append('single_frame', options.singleFrame.toString())
    if (options?.optimizeGif !== undefined) params.append('optimize_gif', String(options.optimizeGif))
    if (options?.forceOsm) params.append('force_osm', 'true')

    const response = await api.post(`/render/${mockId}?${params.toString()}`)
    return response.data
  },

  // Poll job status
  async getJobStatus(jobId: string): Promise<{ 
    status: string
    progress: number
    estimated_seconds: number
    render_timeout_seconds?: number
    queue_name?: string
    estimate_breakdown?: RenderEstimateBreakdown
    time_remaining: number
    actual_duration_seconds?: number
    current_stage?: string
    worker_retry?: WorkerRetryState | null
    error?: string
    error_code?: string
    error_level?: string
    admin_logs?: {
      stdout_tail?: string
      stderr_tail?: string
      exception?: string
    } | null
    artifacts?: RenderArtifact[]
    result: any 
  }> {
    const response = await api.get(`/render/job/${jobId}`)
    return response.data
  },

  async listRenderArtifacts(jobId: string): Promise<RenderArtifact[]> {
    const response = await api.get(`/render/job/${jobId}/artifacts`)
    return response.data?.artifacts || []
  },

  /**
   * Directly-loadable URLs for a job's frames, keyed by artifact id.
   *
   * The download endpoint is authenticated by an Authorization header, which a
   * browser <img src> cannot send and a navigation cannot carry. These URLs
   * carry their own authority (a signed object URL in production, the static
   * /uploads path in dev), so they work in both.
   */
  async renderArtifactViewUrls(jobId: string): Promise<Record<string, string>> {
    const response = await api.get(`/render/job/${jobId}/artifacts/view-urls`)
    return response.data?.urls || {}
  },

  async downloadRenderArtifact(jobId: string, artifactId: string): Promise<Blob> {
    const response = await api.get(
      `/render/job/${jobId}/artifacts/${encodeURIComponent(artifactId)}/download`,
      { responseType: 'blob' },
    )
    return response.data
  },

  /**
   * Download several frames as one zip.
   *
   * A browser will not honour a burst of programmatic downloads: after the
   * first, the rest are silently blocked or buried behind a prompt. One archive
   * is the only way "download these six frames" reliably yields six files.
   */
  async downloadRenderArtifactsArchive(jobId: string, artifactIds: string[]): Promise<Blob> {
    const response = await api.post(
      `/render/job/${jobId}/artifacts/download`,
      { artifact_ids: artifactIds },
      { responseType: 'blob' },
    )
    return response.data
  },

  // Validate mock assets
  async validateAssets(mockId: string): Promise<{
    valid: boolean
    renderable?: boolean
    missing_assets: any[]
    available_assets: any[]
    required_assets?: string[]
    placeholder_created?: string[]
    reusable_assets?: any[]
    brand_assets_dir?: string
    source_asset_dirs?: string[]
  }> {
    const response = await api.get(`/render/${mockId}/validate`)
    return response.data
  },

  // Render a real renderer2 preview image for a single flow block's messages,
  // reusing the same provisioner templates use. Lets the flow show a correct
  // image for blocks that came from an existing conversation (no wizard insert).
  async renderBlockPreview(
    messages: any[],
    elementType: string,
    mockId?: string,
    cacheKey?: string,
  ): Promise<{
    status: string
    preview_frames: string[]
    display_frames: string[]
    thumbnail_url?: string | null
    reason?: string | null
  }> {
    const response = await api.post('/render/blocks/preview', {
      messages,
      element_type: elementType,
      mock_id: mockId,
      cache_key: cacheKey,
    }, {
      // A preview is advisory UI, not a long-running final render. Always reach
      // a terminal editor state if the preview worker is unavailable or wedged.
      timeout: 30_000,
    })
    return response.data
  },

  // Validate conversation JSON with renderer validator
  async validateConversation(mockId: string, conversationJson: any): Promise<{
    valid: boolean
    exit_code: number
    output: string
    errors: string[]
    warnings: string[]
    issues?: ValidatorIssue[]
    summary?: ValidatorSummaryItem[]
    recommendations?: string[]
    validation_skipped?: boolean
    used_brand?: string
    used_legacy_asset_fallbacks?: string[]
  }> {
    const response = await api.post(`/render/${mockId}/validate-conversation`, conversationJson)
    return response.data
  },

  // Apply the validator's safe auto-fixes; returns the fixed conversation plus
  // what changed (applied) and what still needs a human (unfixable). Does not
  // persist — the caller applies the result to the editor.
  async autofixConversation(mockId: string, conversationJson: any): Promise<{
    conversation_json: any
    applied?: string[]
    unfixable?: string[]
    error?: string
  }> {
    const response = await api.post(`/render/${mockId}/autofix-conversation`, conversationJson)
    return response.data
  },

  // Upload mock/use case thumbnail
  async uploadThumbnail(mockId: string, file: File): Promise<Mock> {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post(`/mocks/${mockId}/thumbnail`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Delete a version
  async deleteVersion(mockId: string, versionId: string): Promise<void> {
    await api.delete(`/mocks/${mockId}/versions/${versionId}`)
  },

  // Edit locking
  async lockMock(mockId: string): Promise<{
    locked: boolean
    locked_by: string
    locked_by_id: string
    locked_at: string
    can_edit: boolean
  }> {
    const response = await api.post(`/mocks/${mockId}/lock`)
    return response.data
  },

  async unlockMock(mockId: string): Promise<{ unlocked: boolean }> {
    const response = await api.post(`/mocks/${mockId}/unlock`)
    return response.data
  },

  // Owner lock (permanent — only unlockable by lock holder or admin)
  async ownerLockMock(mockId: string): Promise<{ locked: boolean; locked_by: string; locked_at: string }> {
    const response = await api.post(`/mocks/${mockId}/owner-lock`)
    return response.data
  },

  async ownerUnlockMock(mockId: string): Promise<{ locked: boolean }> {
    const response = await api.post(`/mocks/${mockId}/owner-unlock`)
    return response.data
  },

  async sendHeartbeat(mockId: string): Promise<any> {
    const response = await api.post(`/mocks/${mockId}/heartbeat`)
    return response.data
  },
}
